// External libraries
import ls from "localstorage-slim";
import React, { Component } from "react";

// Microsoft Graph Toolkit
import { Providers } from "@microsoft/mgt-element";

// Kendo React components
import { ComboBox, MultiSelect } from "@progress/kendo-react-dropdowns";

// Signals
import { allDatasets } from "../Store/CommonSignals";

export class CustomPeoplePicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: "",
      searchResults: [],
      suggestedPeople: [],
      userGroups: [],
      isLoading: false,
      isLoadingSuggestions: false,
      error: null,
    };
    this.debounceTimeout = null;
  }

  componentDidMount() {
    this.loadSuggestedPeople();
    this.loadUserGroups();
  }

  componentWillUnmount() {
    clearTimeout(this.debounceTimeout);
  }

  formatGroupsForPicker = (groups) => {
    return groups.map((group) => ({
      id: `group_${group.id}`,
      displayName: group.datasetName,
      email: `${group.values?.length || 0} members`,
      isGroup: true,
      groupId: group.id,
      members:
        group.values
          ?.map((v) => {
            try {
              const person = typeof v.value === "string" ? JSON.parse(v.value) : v.person;
              return person;
            } catch {
              // Ignore parsing errors
              return null;
            }
          })
          .filter(Boolean) || [],
    }));
  };

  loadUserGroups = () => {
    const { enableGroups } = this.props;
    if (!enableGroups) return;

    const groups =
      allDatasets.value?.filter((dataset) => dataset.datasetType === "User Group") || [];
    this.setState({ userGroups: this.formatGroupsForPicker(groups) });
  };

  loadSuggestedPeople = async () => {
    this.setState({ isLoadingSuggestions: true });

    try {
      const suggestedPeople = ls.get("suggestedPeople");

      // Check cached suggested people
      if (JSON.tryParse(suggestedPeople)) {
        this.setState({
          suggestedPeople: JSON.tryParse(suggestedPeople),
          isLoadingSuggestions: false,
        });

        this.fetchUserPhotos(JSON.tryParse(suggestedPeople));
        return;
      } else {
        const provider = Providers.globalProvider;
        if (!provider?.graph) {
          return;
        }

        const response = await provider.graph
          .api("/me/people")
          .select("id,displayName,scoredEmailAddresses,userPrincipalName")
          .top(10)
          .get();

        const people = response?.value || [];

        const formattedPeople = people.map((person) => ({
          id: person.id,
          displayName: person.displayName || "",
          email: person.scoredEmailAddresses?.[0]?.address || person.userPrincipalName || "",
          personImage: null,
        }));

        // Cache suggested people with 24h ttl
        ls.set("suggestedPeople", JSON.stringify(formattedPeople), { ttl: 60 * 60 * 24 });

        this.setState({ suggestedPeople: formattedPeople, isLoadingSuggestions: false });
        this.fetchUserPhotos(formattedPeople);
      }
    } catch {
      this.setState({ isLoadingSuggestions: false });
    }
  };

  searchUsers = async (searchText) => {
    if (!searchText || searchText.trim().length < 2) {
      this.setState({ searchResults: [], error: null });
      return;
    }

    this.setState({ isLoading: true, error: null });

    try {
      const provider = Providers.globalProvider;
      if (!provider?.graph) {
        throw new Error("Graph provider is not available");
      }

      // Fetch users
      const usersPromise = provider.graph
        .api("/users")
        .header("ConsistencyLevel", "eventual")
        .search(`"displayName:${searchText}" OR "mail:${searchText}"`)
        .select("id,displayName,mail,userPrincipalName")
        .top(10)
        .get();

      // Fetch groups (always fetched alongside users)
      const groupsPromise = provider.graph
        .api("/groups")
        .header("ConsistencyLevel", "eventual")
        .search(`"displayName:${searchText}"`)
        .select("id,displayName,mail")
        .top(10)
        .get();

      const [usersResponse, groupsResponse] = await Promise.all([usersPromise, groupsPromise]);

      const users = usersResponse?.value || [];
      const groups = groupsResponse?.value || [];

      const formattedUsers = users.map((user) => ({
        id: user.id,
        displayName: user.displayName || "",
        email: user.userPrincipalName || user.mail || "",
        personImage: null,
      }));

      // Graph groups formatted identically to users
      const formattedGroups = groups.map((group) => ({
        id: group.id,
        displayName: group.displayName || "",
        email: group.mail || "Group",
        personImage: null,
      }));

      const combinedResults = [...formattedGroups, ...formattedUsers];
      this.setState({ searchResults: combinedResults, isLoading: false });
      this.fetchUserPhotos(formattedUsers); // Photos are only fetched for users
    } catch {
      this.setState({
        error: "Error fetching users or groups. Please try again.",
        isLoading: false,
        searchResults: [],
      });
    }
  };

  fetchUserPhotos = async (users) => {
    const provider = Providers.globalProvider;
    if (!provider?.graph) return;
    if (!users) return;

    const photoPromises = users.map(async (user) => {
      try {
        const response = await provider.graph.api(`/users/${user.id}/photo/$value`).get();

        if (response instanceof Response || response instanceof Blob) {
          const blob = response instanceof Response ? await response.blob() : response;
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({ id: user.id, personImage: reader.result });
            };
            reader.readAsDataURL(blob);
          });
        }
        return { id: user.id, personImage: null };
      } catch {
        return { id: user.id, personImage: null };
      }
    });

    const photoResults = await Promise.all(photoPromises);

    this.setState((prevState) => {
      const updatedResults = prevState.searchResults?.map((user) => {
        const photoData = photoResults.find((p) => p.id === user.id);
        return photoData?.personImage ? { ...user, personImage: photoData.personImage } : user;
      });
      const updatedSuggestions = prevState.suggestedPeople?.map((user) => {
        const photoData = photoResults.find((p) => p.id === user.id);
        return photoData?.personImage ? { ...user, personImage: photoData.personImage } : user;
      });
      return { searchResults: updatedResults, suggestedPeople: updatedSuggestions };
    });
  };

  handleFilterChange = (e) => {
    const searchText = e.filter.value || "";
    this.setState({ searchText });

    clearTimeout(this.debounceTimeout);

    if (searchText.trim().length < 2) {
      this.setState({ searchResults: [], error: null });
      this.loadUserGroups();
      return;
    }

    this.debounceTimeout = setTimeout(() => {
      this.searchUsers(searchText);
      this.searchGroups(searchText);
    }, 400);
  };

  searchGroups = (searchText) => {
    const { enableGroups } = this.props;
    if (!enableGroups) return;

    const groups =
      allDatasets.value?.filter((dataset) => dataset.datasetType === "User Group") || [];
    const filteredGroups = groups.filter((group) =>
      group.datasetName?.toLowerCase().includes(searchText.toLowerCase())
    );
    this.setState({ userGroups: this.formatGroupsForPicker(filteredGroups) });
  };

  handleMultiSelectChange = (selectedPeople) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange({
        target: {
          selectedPeople: selectedPeople,
        },
      });
    }
  };

  handleSingleSelectChange = (selectedItem) => {
    const { onChange, enableGroups } = this.props;
    this.setState({ searchText: "", searchResults: [] });

    if (!onChange) return;

    if (enableGroups && selectedItem?.isGroup) {
      onChange({
        target: {
          selectedPeople: selectedItem.members || [],
          selectedGroup: selectedItem,
          isGroupSelection: true,
        },
      });
    } else {
      const selectedPeople = selectedItem ? [selectedItem] : [];
      onChange({
        target: {
          selectedPeople: selectedPeople,
          isGroupSelection: false,
        },
      });
    }
  };

  handleChange = (e) => {
    const { selectionMode } = this.props;

    if (selectionMode === "multiple") {
      this.handleMultiSelectChange(e.value || []);
    } else {
      this.handleSingleSelectChange(e.value);
    }
  };

  handleRemovePerson = (personToRemove) => {
    const { selectionMode, onChange, selectedPeople } = this.props;

    if (!onChange) return;

    if (selectionMode === "multiple") {
      const selectedArray = Array.isArray(selectedPeople) ? selectedPeople : [];
      const updatedPeople = selectedArray.filter((p) => p.id !== personToRemove.id);
      onChange({
        target: {
          selectedPeople: updatedPeople,
        },
      });
    } else {
      onChange({
        target: {
          selectedPeople: [],
        },
      });
    }
  };

  handleBlur = () => {
    const { onBlur } = this.props;
    if (onBlur) {
      onBlur();
    }
  };

  renderAvatar = (item, size = 32) => {
    if (item?.isGroup) {
      return (
        <div
          className="custom-people-picker-avatar-fallback"
          style={{
            width: size,
            height: size,
            fontSize: size * 0.4,
            backgroundColor: "#0078d4",
            color: "white",
          }}
        >
          G
        </div>
      );
    }

    if (item?.personImage) {
      return (
        <img
          src={item.personImage}
          alt={item.displayName}
          className="custom-people-picker-avatar"
          style={{ width: size, height: size }}
        />
      );
    }

    const initials =
      item?.displayName
        ?.split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase() || "?";

    return (
      <div
        className="custom-people-picker-avatar-fallback"
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {initials}
      </div>
    );
  };

  itemRender = (li, itemProps) => {
    const item = itemProps.dataItem;
    const itemChildren = (
      <div className="custom-people-picker-item">
        {this.renderAvatar(item, 36)}
        <div className="custom-people-picker-item-info">
          <span className="custom-people-picker-item-name">
            {item.isGroup ? `📁 ${item.displayName}` : item.displayName}
          </span>
          <span className="custom-people-picker-item-email">
            {item.isGroup ? `Group - ${item.email}` : item.email}
          </span>
        </div>
      </div>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  valueRender = (element, value) => {
    if (!value) {
      return element;
    }

    const children = (
      <div className="custom-people-picker-value">
        {this.renderAvatar(value, 24)}
        <span>
          {value.displayName} {value.email ? `(${value.email})` : ""}
        </span>
      </div>
    );

    return React.cloneElement(element, { ...element.props }, children);
  };

  tagRender = (tagData, li) => {
    const person = tagData.data[0];
    const children = (
      <div className="custom-people-picker-tag">
        {this.renderAvatar(person, 20)}
        <span>{person?.displayName || ""}</span>
      </div>
    );
    return React.cloneElement(li, li.props, children);
  };

  renderSelectedPerson = (person) => {
    const { disabled } = this.props;

    return (
      <div className="custom-people-picker-selected-person">
        {this.renderAvatar(person, 32)}
        <div className="custom-people-picker-selected-info">
          <span className="custom-people-picker-selected-name">{person.displayName}</span>
          <span className="custom-people-picker-selected-email">{person.email}</span>
        </div>
        {!disabled && (
          <button
            type="button"
            className="custom-people-picker-remove-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.handleRemovePerson(person);
            }}
            title="Remove Person"
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  render() {
    const {
      id,
      name,
      selectedPeople,
      selectionMode,
      disabled,
      valid,
      "data-testid": dataTestId,
      enableGroups,
    } = this.props;
    const { searchResults, suggestedPeople, userGroups, isLoading, searchText } = this.state;

    let selectedArray = [];
    if (selectedPeople) {
      selectedArray = Array.isArray(selectedPeople) ? selectedPeople : [selectedPeople];
    }

    const allItems = [...selectedArray];

    const peopleToShow = searchText.trim().length > 0 ? searchResults : suggestedPeople;

    if (enableGroups && userGroups.length > 0) {
      userGroups.forEach((group) => {
        if (!allItems.some((p) => p.id === group.id)) {
          allItems.push(group);
        }
      });
    }

    peopleToShow?.forEach((result) => {
      if (!allItems.some((p) => p.id === result.id)) {
        allItems.push(result);
      }
    });

    const commonProps = {
      id,
      name,
      "data-testid": dataTestId,
      data: allItems,
      textField: "displayName",
      dataItemKey: "id",
      filterable: true,
      onFilterChange: this.handleFilterChange,
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      disabled,
      valid,
      loading: isLoading,
      itemRender: this.itemRender,
      style: { width: "100%", minWidth: "250px" },
    };

    const hasSingleSelection = selectionMode !== "multiple" && selectedArray.length > 0;

    return (
      <div className="custom-people-picker-container">
        {hasSingleSelection ? (
          this.renderSelectedPerson(selectedArray[0])
        ) : (
          <>
            {selectionMode === "multiple" ? (
              <MultiSelect
                {...commonProps}
                value={selectedArray}
                tagRender={this.tagRender}
                placeholder="Search people..."
              />
            ) : (
              <ComboBox
                {...commonProps}
                value={selectedArray[0] || null}
                valueRender={this.valueRender}
                placeholder="Search People..."
              />
            )}
            {isLoading && <span className="custom-people-picker-loading">Searching...</span>}
          </>
        )}
      </div>
    );
  }
}
